import { useEffect, useRef, useState, useCallback } from "react";
import WeeklyRevenue from "./components/WeeklyRevenue";
import TotalSpent from "./components/TotalSpent";
import Pdf1 from "../../../assets/Docs/POSP_Training.pdf";
import Pdf2 from "../../../assets/Docs/POSP_Certificate.pdf";
import axios from "axios";
import { BASE_URL } from "../../../baseurl";
import Step from "./components/Step";
import { number } from "yup";
import Loader from "../marketplace/components/Loader";
import { IoClose } from "react-icons/io5";

// Utility to convert seconds to HH:mm:ss
const secondsToTimeFormat = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// Utility to convert HH:mm:ss to seconds
const timeFormatToSeconds = (timeStr) => {
  const [hours, minutes, seconds] = timeStr.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [data, setData] = useState({});
  const [updatedVideoCounts, setUpdatedVideoCounts] = useState(new Set());
  const [showSteps, setShowSteps] = useState(false); // For mobile toggle
  const videoRef = useRef(null);
  const timeRef = useRef(0);
  const needsUpdate = useRef(false);
  const [isVisible, setIsVisible] = useState(true);

  const getData = async () => {
    try {
      let token = localStorage.getItem("user_token");
      let res = await axios.get(`${BASE_URL}/user/data/getDetails`, {
        headers: { Authorization: token },
      });
      if (res?.status === 200) {
        setData(res?.data?.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch videos and initialize steps
  const getVideo = async () => {
    try {
      setLoading(true);
      setLoadingMessage({
        title: "Loading...",
        message: "It won't be long!",
      });
      const token = localStorage.getItem("user_token");
      const res = await axios.get(`${BASE_URL}/user/video/getAllVideo`, {
        headers: { Authorization: token },
      });

      const transformData = (inputData) =>
        inputData.map((item, index) => ({
          id: index + 1,
          title: item.fileName,
          active: index === 0,
          path: item.fileUrl,
          completed: false,
        }));

      const videoData = transformData(res?.data?.data);
      setSteps(videoData);

      if (res.status === 200) {
        await getVideoCount(videoData);
      }
    } catch (e) {
      console.error("Error fetching videos:", e);
    } finally {
      setLoading(false);
    }
  };

  // Fetch video count and time from API
  const getVideoCount = async (videoData) => {
    try {
      const token = localStorage.getItem("user_token");
      const res = await axios.get(`${BASE_URL}/user/video/getVideoCount`, {
        headers: { Authorization: token },
      });
      const { videoCount = 0, videoTime = "00:00:00" } = res.data?.data || {};

      const videoTimeSeconds = timeFormatToSeconds(videoTime);

      setSteps((prevSteps) =>
        prevSteps.map((step, index) => {
          if (index < videoCount) {
            return { ...step, completed: true, active: true };
          } else if (index === videoCount) {
            return { ...step, active: true, completed: false };
          }
          return { ...step, active: false, completed: false };
        })
      );

      setUpdatedVideoCounts(
        new Set([...Array(videoCount).keys()].map((i) => i + 1))
      );

      setCurrentVideo(videoCount);
      timeRef.current = videoTimeSeconds;
      needsUpdate.current = false; // Reset update flag
    } catch (e) {
      console.error("Error fetching video count:", e);
    }
  };

  // Save video count and time to API - only called on page close/route change
  const setVideoCount = async (count, timeSeconds) => {
    // Don't exceed the total number of videos
    const maxCount = Math.min(count, steps.length);

    // Only skip if the count is already updated AND the time hasn't changed
    if (
      maxCount > steps.length ||
      (updatedVideoCounts.has(maxCount) &&
        timeSeconds === timeRef.current &&
        !needsUpdate.current)
    ) {
      console.log(
        `Skipping API call: count ${maxCount} already updated or invalid, time ${timeSeconds}`
      );
      return;
    }
    try {
      const token = localStorage.getItem("user_token");
      const timeFormatted = secondsToTimeFormat(timeSeconds);
      const isLastVideo = count === steps.length;
      console.log(
        `Calling updateCount API with count: ${maxCount}, time: ${timeFormatted}`
      );
      let payload = {
        videoCount: count,
        videoTime: timeFormatted,
        ...(isLastVideo && { videosWatched: true }),
      };
      const res = await axios.put(
        `${BASE_URL}/user/video/updateCount`,
        payload,
        { headers: { Authorization: token } }
      );
      console.log(`API response status: ${res.status}`);

      if (res.status === 200) {
        setSteps((prevSteps) =>
          prevSteps.map((step, index) => {
            if (index < maxCount) {
              return { ...step, completed: true, active: true };
            } else if (index === maxCount) {
              return { ...step, active: true, completed: false };
            }
            return { ...step, active: false, completed: false };
          })
        );
        setUpdatedVideoCounts((prev) => new Set(prev).add(maxCount));
        needsUpdate.current = false;
      } else {
        console.warn(`Unexpected status code: ${res.status}`);
      }
    } catch (e) {
      console.error("Error updating video count:", e);
    }
  };

  // Handle video completion
  const videoIsComplete = async (id) => {
    console.log(`Video ${id} completed, updating steps and count`);

    setSteps((prevSteps) =>
      prevSteps.map((step, index) => {
        if (index === id) {
          return { ...step, completed: true };
        }
        return step;
      })
    );

    // Prevent multiple API calls for the same video completion
    if (updatedVideoCounts.has(id + 1)) {
      console.log(`Video ${id} already marked as completed, skipping API call`);
      return;
    }

    if (id + 1 < steps.length) {
      setCurrentVideo(id + 1);
      timeRef.current = 0;
      needsUpdate.current = true;
      // Save immediately on video completion
      await setVideoCount(id + 1, 0);
    } else {
      // Last video completed - mark as completed but don't increment count beyond total
      setCurrentVideo(id);
      needsUpdate.current = true;
      // Save the completion of the last video
      await setVideoCount(steps.length, 0);
    }
  };

  // Handle seeking restriction
  const handleSeeked = () => {
    if (
      videoRef.current &&
      steps?.[currentVideo] &&
      !steps[currentVideo].completed
    ) {
      if (timeRef.current < videoRef.current.currentTime) {
        videoRef.current.currentTime = timeRef.current;
      }
    }
  };

  // Update timestamp but don't save to API immediately
  const handleTimeUpdate = () => {
    if (
      videoRef.current &&
      !videoRef.current.seeking &&
      !steps?.[currentVideo]?.completed
    ) {
      timeRef.current = Math.floor(videoRef.current.currentTime);
      needsUpdate.current = true; // Mark that we need to update on page close
    }
  };

  // Change active video
  const changeActive = (id) => {
    if (steps?.[id - 1]?.active) {
      setCurrentVideo(id - 1);
      timeRef.current = 0;
      needsUpdate.current = true;
      setShowSteps(false); // Close mobile menu when video is selected
    }
  };

  // Save progress on page close/route change
  const saveProgress = async () => {
    if (
      needsUpdate.current &&
      videoRef.current &&
      !steps?.[currentVideo]?.completed
    ) {
      timeRef.current = Math.floor(videoRef.current.currentTime);
      await setVideoCount(currentVideo, timeRef.current);
    }
  };

  // Handle page visibility change and beforeunload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveProgress();
      } else if (document.visibilityState === "visible") {
        // Page became visible again - refresh data
        if (steps.length > 0) {
          getVideoCount(steps);
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (needsUpdate.current) {
        saveProgress();
        // For some browsers, you might want to show a confirmation dialog
        // e.preventDefault();
        // e.returnValue = '';
      }
    };

    // Handle route changes (if using React Router)
    const handleRouteChange = () => {
      saveProgress();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // If using React Router, you can add route change listener
    // window.addEventListener("popstate", handleRouteChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // window.removeEventListener("popstate", handleRouteChange);
    };
  }, [currentVideo, steps]);

  // Load videos and data on mount
  useEffect(() => {
    getVideo();
    getData();
  }, []);

  // Set video time when currentVideo changes or video is loaded
  useEffect(() => {
    if (videoRef.current && steps?.[currentVideo]) {
      const setVideoTime = () => {
        if (videoRef.current && videoRef.current.readyState >= 2) {
          try {
            videoRef.current.currentTime = timeRef.current;
          } catch (e) {
            console.error("Error setting video currentTime:", e);
          }
        }
      };

      if (videoRef.current.readyState >= 2) {
        setVideoTime();
      } else {
        videoRef.current.addEventListener("loadeddata", setVideoTime, {
          once: true,
        });
      }

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener("loadeddata", setVideoTime);
        }
      };
    }
  }, [currentVideo, steps]);

  return (
    <div>
      <div
        className={`${
          isVisible ? "" : "hidden"
        } flex items-center justify-between p-0 md:p-1 bg-blue-50 border-2 border-blue-800 rounded-lg shadow-md m-8`}
      >
        <p className="text-lg md:text-xl text-blue-800 font-medium my-auto !pb-4 ml-4">
          For the best experience, please use the Chrome browser.
        </p>
        <button
          onClick={() => setIsVisible(false)}
          className="text-blue-800 hover:text-blue-600 focus:outline-none"
          aria-label="Close"
        >
          <IoClose size={18} />
        </button>
      </div>
      {loading && (
        <div className="mt-12">
          <Loader {...loadingMessage} />
        </div>
      )}
      <div className="container pt-2 m-auto bg_kyc">
        {!loading && data?.ckyc ? (
          <div className="w-full mt-[50px]">
            {/* Mobile Toggle Button */}
            <div className="md:hidden mb-4 px-4">
              <button
                onClick={() => setShowSteps(!showSteps)}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-between"
              >
                <span>Training Steps</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${
                    showSteps ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            {/* Mobile Steps Dropdown */}
            {showSteps && (
              <div className="md:hidden mb-4 px-4">
                <div className="bg-white rounded-lg shadow-lg p-4 max-h-60 overflow-y-auto">
                  {steps.map((step) => (
                    <Step
                      key={step.id}
                      number={step.id}
                      title={step.title}
                      active={step.active}
                      completed={step.completed}
                      changeActive={changeActive}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Main Content Layout */}
            <div className="flex flex-col md:flex-row md:justify-center w-full px-4 md:px-0">
              {/* Desktop Sidebar */}
              <div className="hidden md:block relative form_sidebar">
                <div className="text-black w-full flex flex-col gap-2 p-4">
                  {steps.map((step) => (
                    <Step
                      key={step.id}
                      number={step.id}
                      title={step.title}
                      active={step.active}
                      completed={step.completed}
                      changeActive={changeActive}
                    />
                  ))}
                </div>
              </div>

              {/* Video Content */}
              <div className="w-full md:w-[600px] lg:w-[800px] xl:w-[900px] bg-white rounded-lg shadow-lg">
                {/* Video Player */}
                <div className="p-4 md:p-6">
                  <div
                    className="relative w-full"
                    style={{ paddingBottom: "56.25%" }}
                  >
                    <video
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      controls
                      ref={videoRef}
                      src={steps?.[currentVideo]?.path}
                      preload="auto"
                      onEnded={() => videoIsComplete(currentVideo)}
                      type="video/mp4"
                      onSeeking={handleSeeked}
                      onTimeUpdate={handleTimeUpdate}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Video Title */}
                  <div className="mt-4">
                    <h2 className="text-lg md:text-2xl font-semibold text-gray-800 break-words">
                      {steps?.[currentVideo]?.title}
                    </h2>
                  </div>

                  {/* Mobile Progress Indicator */}
                  <div className="md:hidden mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Progress:</span>
                      <span className="font-semibold">
                        {currentVideo + 1} of {steps.length}
                      </span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${
                            ((currentVideo + 1) / steps.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !loading ? (
          <div className="flex justify-center items-center min-h-[400px] px-4">
            <div className="p-8 bg-white rounded-lg shadow-lg flex flex-col items-center gap-4 max-w-md w-full text-center">
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-16 h-16 md:w-20 md:h-20 text-orange-500 mx-auto"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <p className="text-orange-500 font-semibold text-base md:text-lg">
                Please complete your KYC process to access the training videos.
              </p>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
